import { HasuraMutations } from "@/api/hasura/mutations"
import { HasuraQueries } from "@/api/hasura/queries"
import { MutationResult, QueryResult } from "@/api/utils/types"
import { useQuery, useMutation } from "@/api/utils/useQuery"
import {
  CallTraceAuditConfirmedStatus,
  GdprFieldParser,
} from "@/models/gdprFields/gdprFields.parser"
import { IGdprField } from "@/models/gdprFields/gdprFields.types"
import { IContact } from "@/models/contacts/contact.types"

interface ICallTraceAuditUpdateField {
  id: number
  confirmStatus: CallTraceAuditConfirmedStatus
}
export interface IGdprFieldsService {
  getGdprFields(accountNumber: string): QueryResult<IGdprField[]>
  updateGdprFields(): MutationResult<ICallTraceAuditUpdateField[]>
  updateModifiedFields(
    gdprFields: IGdprField[],
    contact?: Partial<IContact>,
  ): void
}

class GdprFieldsServiceClass implements IGdprFieldsService {
  getGdprFields(accountNumber: string): QueryResult<IGdprField[]> {
    return useQuery({
      ...HasuraQueries.account.gdprFields(accountNumber),
      useRawResponse: true,
      placeholderData: [],
      parserFn: (rawData) => {
        if (!rawData) return []

        return rawData.call_trace_audit.map((rawField) =>
          GdprFieldParser.toDomain(rawField),
        )
      },
    })
  }

  updateGdprFields(): MutationResult<ICallTraceAuditUpdateField[]> {
    return useMutation({
      ...HasuraMutations.account.updateGdprFields(),
      serializerFn: (data) => {
        const serialized = data.map((gdprField) => ({
          where: { id: { _eq: gdprField.id } },
          _set: { confirm_status: gdprField.confirmStatus },
        }))
        return { updates: serialized }
      },
    })
  }

  updateModifiedFields(gdprFields: IGdprField[], contact?: Partial<IContact>) {
    if (!gdprFields.length || !contact?.juniferPrimaryContact) return

    const updates = gdprFields.reduce<ICallTraceAuditUpdateField[]>(
      (prev, current) => {
        if (!contact[current.gdprType]) return prev

        const isSameValue = contact[current.gdprType] === current.dataUpdated
        return [
          ...prev,
          {
            id: current.id,
            confirmStatus: isSameValue ? "CONFIRMED" : "ALTERNATIVE",
          },
        ]
      },
      [],
    )

    this.updateGdprFields().mutate(updates)
  }
}

export const GdprFieldsService = new GdprFieldsServiceClass()
